import { useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import PublicIcon from '@mui/icons-material/Public'

import { MeetingLinkOrder } from '@/core/models/meeting-link-order.model'
import { ConsultationStatus, ConsultationType } from '@/core/models/consultation.model'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { isStaff } from '@/utils/type-guards'

import { useCreateConsultationFromOrder } from '../hooks/useCreateConsultationFromOrder'
import WaitingRoomPicker from './WaitingRoomPicker'

type StartConsultationDialogProps = {
  open: boolean
  onClose: () => void
  order: MeetingLinkOrder
}

type DialogView = 'options' | 'waiting-room'

export default function StartConsultationDialog({ open, onClose, order }: StartConsultationDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const authUser = useAppSelector(selectAuthUser)

  const [view, setView] = useState<DialogView>('options')

  const isPds = isStaff(authUser) && authUser?.status === 'PDS'
  const isPastDate = dayjs(order.startDate).isBefore(dayjs())
  const hasPatient = !!order.createdBy?._id

  const { execute: executeLocal, isLoading } = useCreateConsultationFromOrder({
    order,
    onSuccess: onClose,
  })

  const handleLocal = () => {
    if (!hasPatient) return
    executeLocal({
      patient: order.createdBy._id,
      type: ConsultationType.Local,
      status: ConsultationStatus.Active,
      startDate: new Date().toISOString(),
    })
  }

  const handleRedirectToWizard = () => {
    const params = new URLSearchParams({
      patient: order.createdBy._id,
      speciality: order.speciality,
      orderId: order._id,
    })
    navigate(`/consultations/create?${params.toString()}`)
    onClose()
  }

  const patientName = order.createdBy
    ? `${order.createdBy.userInfo?.name} ${order.createdBy.userInfo?.surname}`
    : '-'

  const handleDialogClose = () => {
    setView('options')
    onClose()
  }

  if (view === 'waiting-room') {
    return (
      <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <WaitingRoomPicker
            order={order}
            onBack={() => setView('options')}
            onComplete={handleDialogClose}
          />
        </DialogContent>
      </Dialog>
    )
  }

  const options = [
    {
      id: 'local' as const,
      icon: <LocalHospitalIcon sx={{ fontSize: 28 }} />,
      title: t('page.agenda.startConsultationDialog.option.local.title'),
      description: t('page.agenda.startConsultationDialog.option.local.description'),
      onClick: handleLocal,
      disabled: !hasPatient || isLoading,
      visible: true,
      instant: true,
    },
    {
      id: 'waiting-room' as const,
      icon: <AccessTimeIcon sx={{ fontSize: 28 }} />,
      title: t('page.agenda.startConsultationDialog.option.waitingRoom.title'),
      description: t('page.agenda.startConsultationDialog.option.waitingRoom.description'),
      onClick: () => setView('waiting-room'),
      disabled: !hasPatient || isLoading,
      visible: isPds,
      instant: true,
    },
    {
      id: 'scheduled' as const,
      icon: <PersonSearchIcon sx={{ fontSize: 28 }} />,
      title: t('page.agenda.startConsultationDialog.option.scheduled.title'),
      description: t('page.agenda.startConsultationDialog.option.scheduled.description'),
      onClick: handleRedirectToWizard,
      disabled: isPastDate || !hasPatient || isLoading,
      disabledReason: isPastDate ? t('page.agenda.startConsultationDialog.option.scheduled.disabled') : undefined,
      visible: true,
      instant: false,
    },
    {
      id: 'network' as const,
      icon: <PublicIcon sx={{ fontSize: 28 }} />,
      title: t('page.agenda.startConsultationDialog.option.network.title'),
      description: t('page.agenda.startConsultationDialog.option.network.description'),
      onClick: handleRedirectToWizard,
      disabled: !hasPatient || isLoading,
      visible: true,
      instant: false,
    },
  ]

  const visibleOptions = options.filter((opt) => opt.visible)

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">{t('page.agenda.startConsultationDialog.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('page.agenda.startConsultationDialog.subtitle')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {/* Order context banner */}
        <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1.5, mb: 2 }}>
          <Typography variant="subtitle2">{patientName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {order.speciality} · {dayjs(order.startDate).format('DD/MM/YYYY HH:mm')}
            {order.meetingPlace?.description ? ` · ${order.meetingPlace.description}` : ''}
          </Typography>
        </Box>

        {/* Option cards grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
          {visibleOptions.map((option) => {
            const isCurrentLoading = isLoading && option.id === 'local'
            const card = (
              <Button
                key={option.id}
                variant="outlined"
                onClick={option.onClick}
                disabled={option.disabled}
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  textTransform: 'none',
                  borderStyle: option.instant ? 'solid' : 'dashed',
                  height: '100%',
                  position: 'relative',
                }}
              >
                {isCurrentLoading && (
                  <CircularProgress size={20} sx={{ position: 'absolute', top: 8, right: 8 }} />
                )}
                <Box sx={{ color: option.disabled ? 'text.disabled' : 'primary.main', mb: 1 }}>
                  {option.icon}
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {option.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                  {option.description}
                </Typography>
              </Button>
            )

            if (option.disabledReason) {
              return (
                <Tooltip key={option.id} title={option.disabledReason} arrow>
                  <span>{card}</span>
                </Tooltip>
              )
            }

            return card
          })}
        </Box>

        <Stack direction="row" justifyContent="flex-end">
          <Button onClick={handleDialogClose} color="inherit">
            {t('common.cancel')}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
